import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine} from '@dropbox/dig-icons/assets';
import {EmployeeRoleLevel, RedactedWorkstreamMetric, WorkstreamMetric} from 'client';
import {CompactMetricValueText} from 'components/DSYS/CompactMetricValueText';
import {OptionalTooltip} from 'components/DSYS/OptionalTooltip';
import {Column, Table} from 'components/DSYS/Table';
import {CURRENT_QUARTER, CURRENT_YEAR} from 'constant';
import {t} from 'i18next';
import {createContext, Fragment, useContext, useMemo, useState} from 'react';
import {
  getNorthStarMetricOrdering,
  isFullWorkstreamMetric,
  isNorthStarMetricType,
  isPrimaryMetric,
} from 'views/workstreams/utils';

import {RedactedMetricValuePlaceholder} from './RedactedMetricValuePlaceholder';
import type {DetailsTableMetric} from './workstreamDetailsTableMetric';
import {
  logWorkstreamMetricsSubworkstreamsExpanded,
  type WorkstreamMetricStatusChangeSource,
} from './workstreamMetricAnalytics';
import {
  EditableMetricValue,
  getRedactedMetricIdentityProjection,
  MetricStatusControl,
  type WorkstreamMetricChangeHandler,
  WorkstreamMetricNameCellContent,
} from './WorkstreamRedesignSections';
import styles from './WorkstreamTable.module.css';

export const METRIC_QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4'] as const;
export type MetricQuarter = (typeof METRIC_QUARTERS)[number];
export type {DetailsTableMetric} from './workstreamDetailsTableMetric';
type QuarterTableMetric = DetailsTableMetric | RedactedWorkstreamMetric;

/** Clamp a 1-based quarter number (e.g. CURRENT_QUARTER) to a valid MetricQuarter. */
export const getDefaultMetricQuarter = (currentQuarter: number): MetricQuarter =>
  METRIC_QUARTERS[Math.min(Math.max(currentQuarter - 1, 0), METRIC_QUARTERS.length - 1)];

/** Whether the selected period has ended relative to the client calendar quarter. */
export const isPastMetricQuarter = (year: number, quarter: MetricQuarter) =>
  year < CURRENT_YEAR ||
  (year === CURRENT_YEAR && METRIC_QUARTERS.indexOf(quarter) < CURRENT_QUARTER - 1);

const getForecast = (metric: WorkstreamMetric, period: string) =>
  metric.metric_forecasts?.find((forecast) => forecast.period === period);

const headerLabel = (label: string) => (
  <Text isBold size="small">
    {label}
  </Text>
);

/** Shared column definitions for every single-quarter metrics table. */
export const getQuarterMetricColumns = (): Column<QuarterTableMetric>[] => [
  {type: 'metric', minWidth: 240, spanner: true},
  {type: 'status', minWidth: 112, width: 132},
  {type: 'baseline', minWidth: 96, width: 120},
  {type: 'value', minWidth: 96, width: 120},
  {type: 'budget', minWidth: 96, width: 120},
  {type: 'target', minWidth: 96, width: 120},
  {type: 'eoy', minWidth: 96, width: 120},
];

export const QUARTER_METRIC_COLUMN_COUNT = getQuarterMetricColumns().length;

export const getQuarterColumnHeaderLabel = (year: number, quarter: MetricQuarter) => ({
  status: headerLabel(t('status')),
  metric: headerLabel(t('metric')),
  baseline: headerLabel(t('baseline')),
  value: headerLabel(
    isPastMetricQuarter(year, quarter) ? `${quarter} ${t('actual')}` : t('current')
  ),
  target: headerLabel(`${quarter} ${t('target')}`),
  budget: headerLabel(`${quarter} ${t('budget')}`),
  eoy: headerLabel(`${t('eoy')} ${t('budget')}`),
});

const valueCell = (value: string | null | undefined) => (
  <Table.Cell style={{padding: 0}}>
    <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="flex-start">
      <CompactMetricValueText value={value} showTooltip={false} />
    </Box>
  </Table.Cell>
);

/** A single metric row, shared by the standalone table and the grouped scorecard. */
// Opt-in (/workstreams's detail page): tint the north star's row, as
// its card's north star box is, so the two read as the same thing.
export const HighlightNorthStarRowContext = createContext(false);

export const QuarterMetricRow = ({
  handleMetricChange,
  metric,
  owner,
  year,
  quarter,
  statusChangeSource,
  workstreamId,
}: {
  metric: QuarterTableMetric;
  owner?: EmployeeRoleLevel;
  year: number;
  quarter: MetricQuarter;
  handleMetricChange?: WorkstreamMetricChangeHandler;
  statusChangeSource?: WorkstreamMetricStatusChangeSource;
  workstreamId?: number;
}) => {
  const fullMetric = isFullWorkstreamMetric(metric) ? metric : null;
  const identityMetric =
    fullMetric ?? getRedactedMetricIdentityProjection(metric as RedactedWorkstreamMetric);
  const forecast = fullMetric ? getForecast(fullMetric, `${year}${quarter}`) : undefined;
  const eoyForecast = fullMetric ? getForecast(fullMetric, `${year}EOY`) : undefined;
  const isPastQuarter = isPastMetricQuarter(year, quarter);
  const highlightNorthStar = useContext(HighlightNorthStarRowContext);
  const isHighlightedNorthStar =
    highlightNorthStar &&
    fullMetric != null &&
    workstreamId != null &&
    isPrimaryMetric(fullMetric, workstreamId) &&
    isNorthStarMetricType(fullMetric.metric_type);
  const renderRedactedValueCell = (key: number) => (
    <Table.Cell key={key} style={{padding: 0}}>
      <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="flex-start">
        <RedactedMetricValuePlaceholder />
      </Box>
    </Table.Cell>
  );

  return (
    <Table.Row
      className={isHighlightedNorthStar ? atoms({backgroundColor: 'Primary Surface'}) : undefined}
    >
      <Table.Cell style={{padding: 0}}>
        <Box width="100%" minHeight="100%" padding="8" display="flex" style={{gap: 4}}>
          <WorkstreamMetricNameCellContent
            metric={identityMetric}
            owner={owner}
            redactedMetric={!fullMetric}
            showPrimaryMetricIcon={Boolean(fullMetric)}
            workstreamId={fullMetric ? workstreamId : undefined}
          />
        </Box>
      </Table.Cell>
      {fullMetric ? (
        <>
          <Table.Cell style={{padding: 0}}>
            <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="flex-start">
              <MetricStatusControl
                metric={fullMetric}
                handleMetricChange={handleMetricChange}
                statusChangeSource={statusChangeSource}
                workstreamId={workstreamId}
              />
            </Box>
          </Table.Cell>
          {valueCell(fullMetric.baseline)}
          {isPastQuarter || !handleMetricChange ? (
            valueCell(isPastQuarter ? (forecast?.actual ?? null) : fullMetric.current)
          ) : (
            <Table.Cell style={{padding: 0}}>
              <Box width="100%" minHeight="100%" padding="8" display="flex" alignItems="flex-start">
                <EditableMetricValue
                  className={styles.redesignMetricsEditableValue}
                  metric={fullMetric}
                  metricTitleText={fullMetric.title}
                  column="current"
                  handleMetricChange={handleMetricChange}
                />
              </Box>
            </Table.Cell>
          )}
          {valueCell(forecast?.budget ?? null)}
          {valueCell(forecast?.target ?? null)}
          {valueCell(eoyForecast?.budget ?? null)}
        </>
      ) : (
        Array.from({length: QUARTER_METRIC_COLUMN_COUNT - 1}, (_, index) =>
          renderRedactedValueCell(index)
        )
      )}
    </Table.Row>
  );
};

export const QuarterTableMetricRow = QuarterMetricRow;

/**
 * Scorecard-style, single-quarter projection of a workstream's metric forecasts.
 * Reuses the annual `metric_forecasts` data (no extra fetch) and the shared
 * scorecard trend/status helpers — it just slices to the selected quarter.
 */
export const WorkstreamMetricsQuarterTable = ({
  handleMetricChange,
  metrics,
  owner,
  year,
  quarter,
  statusChangeSource,
  workstreamId,
}: {
  metrics: QuarterTableMetric[];
  owner?: EmployeeRoleLevel;
  year: number;
  quarter: MetricQuarter;
  handleMetricChange?: WorkstreamMetricChangeHandler;
  statusChangeSource?: WorkstreamMetricStatusChangeSource;
  workstreamId?: number;
}) => {
  const columns = useMemo(() => getQuarterMetricColumns(), []);
  const columnHeaderLabel = useMemo(
    () => getQuarterColumnHeaderLabel(year, quarter),
    [year, quarter]
  );

  return (
    <Table
      columns={columns}
      data={metrics}
      bodyCellPadding={8}
      headerTextSize="small"
      columnHeaderLabel={columnHeaderLabel}
      horizontalLines
      border={false}
      style={{width: '100%'}}
    >
      <Table.Header />
      <Table.Body>
        {metrics.map((metric) => (
          <QuarterTableMetricRow
            key={metric.id}
            metric={metric}
            year={year}
            quarter={quarter}
            handleMetricChange={handleMetricChange}
            owner={owner}
            statusChangeSource={statusChangeSource}
            workstreamId={workstreamId}
          />
        ))}
      </Table.Body>
    </Table>
  );
};

export type QuarterMetricSubGroup = {
  id: number;
  name: string;
  metrics: QuarterTableMetric[];
};

/**
 * In-table group separator row. When `onToggle` is provided it renders a chevron
 * toggle — used for the parent workstream row that shows/hides all sub-workstreams
 * at once. Without it, the row is a static, indented label for a single
 * sub-workstream (the parent toggle governs its visibility).
 */
export const SubWorkstreamHeaderRow = ({
  name,
  expanded = true,
  onToggle,
  columnCount = QUARTER_METRIC_COLUMN_COUNT,
}: {
  name: string;
  expanded?: boolean;
  onToggle?: () => void;
  columnCount?: number;
}) => (
  <Table.Row>
    <Table.Cell colSpan={columnCount} style={{padding: 0}}>
      <Box backgroundColor="Opacity Surface">
        {onToggle ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={expanded ? t('hide_sub_workstreams') : t('show_sub_workstreams')}
            className={atoms({display: 'flex', alignItems: 'center', width: '100%'})}
            style={{
              gap: 6,
              padding: '6px 8px',
              cursor: 'pointer',
              background: 'none',
              border: 'none',
              textAlign: 'left',
            }}
          >
            <OptionalTooltip
              enabled
              title={expanded ? t('hide_sub_workstreams') : t('show_sub_workstreams')}
              placement="top"
              wrapperStyle={{display: 'flex', alignItems: 'center'}}
            >
              <UIIcon
                src={expanded ? ChevronDownLine : ChevronRightLine}
                className={atoms({color: 'Text Subtle'})}
                size="small"
              />
            </OptionalTooltip>
            <Text size="small">{name}</Text>
          </button>
        ) : (
          // Static sub-workstream label, indented to nest under the parent row.
          <Box display="flex" alignItems="center" style={{gap: 6, padding: '6px 8px 6px 30px'}}>
            <Text size="small">{name}</Text>
          </Box>
        )}
      </Box>
    </Table.Cell>
  </Table.Row>
);

/**
 * Single-quarter metrics table that renders a parent workstream's metrics plus any
 * direct sub-workstream metric groups inside one shared table. Unlike rendering a
 * separate {@link WorkstreamMetricsQuarterTable} per group, the column header is
 * drawn once and sub-workstreams are delimited by in-table separator rows — keeping
 * the details view aligned with the list/pillar scorecard layout.
 */
export const WorkstreamMetricsQuarterGroupedTable = ({
  handleMetricChange,
  metrics,
  owner,
  subGroups,
  subWorkstreamsExpanded: controlledSubWorkstreamsExpanded,
  onSubWorkstreamsExpandedChange,
  year,
  quarter,
  statusChangeSource,
  workstreamId,
}: {
  metrics: QuarterTableMetric[];
  owner?: EmployeeRoleLevel;
  subGroups: QuarterMetricSubGroup[];
  subWorkstreamsExpanded?: boolean;
  onSubWorkstreamsExpandedChange?: (expanded: boolean) => void;
  year: number;
  quarter: MetricQuarter;
  handleMetricChange?: WorkstreamMetricChangeHandler;
  statusChangeSource?: WorkstreamMetricStatusChangeSource;
  workstreamId?: number;
}) => {
  const columns = useMemo(() => getQuarterMetricColumns(), []);
  const columnHeaderLabel = useMemo(
    () => getQuarterColumnHeaderLabel(year, quarter),
    [year, quarter]
  );
  // A single chevron next to the parent workstream shows/hides all sub-workstreams
  // (their separator rows and metrics) together, so the view toggles between the
  // current workstream's outcomes only and the full sub-workstream rollup. Collapsed
  // by default so the view opens on just the current workstream's outcomes.
  const [uncontrolledSubWorkstreamsExpanded, setUncontrolledSubWorkstreamsExpanded] =
    useState(false);
  const subWorkstreamsExpanded =
    controlledSubWorkstreamsExpanded ?? uncontrolledSubWorkstreamsExpanded;
  const orderedMetrics: QuarterTableMetric[] = useMemo(() => {
    if (workstreamId == null) {
      return metrics;
    }

    return metrics.every(isFullWorkstreamMetric)
      ? getNorthStarMetricOrdering(metrics as DetailsTableMetric[], workstreamId).orderedMetrics
      : metrics;
  }, [metrics, workstreamId]);
  const orderedSubGroups: QuarterMetricSubGroup[] = useMemo(
    () =>
      subGroups.map((group) => ({
        ...group,
        metrics: group.metrics.every(isFullWorkstreamMetric)
          ? getNorthStarMetricOrdering(group.metrics as DetailsTableMetric[], group.id)
              .orderedMetrics
          : group.metrics,
      })),
    [subGroups]
  );
  const hasSubGroups = orderedSubGroups.length > 0;
  const toggleSubWorkstreams = () => {
    const nextExpanded = !subWorkstreamsExpanded;
    setUncontrolledSubWorkstreamsExpanded(nextExpanded);
    onSubWorkstreamsExpandedChange?.(nextExpanded);
    if (workstreamId != null) {
      logWorkstreamMetricsSubworkstreamsExpanded('details', {
        workstream_id: workstreamId,
        expanded: nextExpanded,
        sub_workstream_count: orderedSubGroups.length,
      });
    }
  };
  const flatData: QuarterTableMetric[] = useMemo(
    () => [
      ...orderedMetrics,
      ...(subWorkstreamsExpanded ? orderedSubGroups.flatMap((group) => group.metrics) : []),
    ],
    [orderedMetrics, orderedSubGroups, subWorkstreamsExpanded]
  );

  return (
    <Table
      columns={columns}
      data={flatData}
      bodyCellPadding={8}
      headerTextSize="small"
      columnHeaderLabel={columnHeaderLabel}
      horizontalLines
      border={false}
      style={{width: '100%'}}
    >
      <Table.Header />
      <Table.Body>
        {orderedMetrics.map((metric) => (
          <QuarterTableMetricRow
            key={metric.id}
            metric={metric}
            year={year}
            quarter={quarter}
            handleMetricChange={handleMetricChange}
            owner={owner}
            statusChangeSource={statusChangeSource}
            workstreamId={workstreamId}
          />
        ))}
        {hasSubGroups ? (
          <SubWorkstreamHeaderRow
            name={t('sub_workstreams').toString()}
            expanded={subWorkstreamsExpanded}
            onToggle={toggleSubWorkstreams}
          />
        ) : null}
        {hasSubGroups && subWorkstreamsExpanded
          ? orderedSubGroups.map((group) => (
              <Fragment key={group.id}>
                <SubWorkstreamHeaderRow name={group.name} />
                {group.metrics.map((metric) => (
                  <QuarterTableMetricRow
                    key={metric.id}
                    metric={metric}
                    year={year}
                    quarter={quarter}
                    handleMetricChange={handleMetricChange}
                    statusChangeSource={statusChangeSource}
                    workstreamId={group.id}
                  />
                ))}
              </Fragment>
            ))
          : null}
      </Table.Body>
    </Table>
  );
};
